import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { apiRequest } from '../config/authConfig';
import { appConfig } from '../config/appConfig';

/**
 * Thrown when no access token could be obtained for an authenticated account
 * (both silent and interactive acquisition failed). Distinguishes "the user
 * needs to sign in again" from unrelated API/network failures so callers can
 * surface a re-auth prompt instead of a generic error.
 */
export class AuthSessionExpiredError extends Error {
  constructor(message = 'Your session has expired. Please sign in again.') {
    super(message);
    this.name = 'AuthSessionExpiredError';
  }
}

// MSAL only allows one interactive auth flow at a time — a second concurrent
// acquireTokenPopup call fails immediately with interaction_in_progress. Share
// a single in-flight popup promise across concurrent getAccessToken callers
// instead of each triggering its own.
let interactiveTokenPromise: Promise<string> | null = null;

function acquireTokenInteractive(msalInstance: IPublicClientApplication): Promise<string> {
  if (!interactiveTokenPromise) {
    interactiveTokenPromise = msalInstance
      .acquireTokenPopup(apiRequest)
      .then((response) => response.accessToken)
      .finally(() => {
        interactiveTokenPromise = null;
      });
  }
  return interactiveTokenPromise;
}

/**
 * Get access token for API calls.
 * Returns null only when authentication is disabled or there's no account to
 * authenticate with. Throws AuthSessionExpiredError if an account exists but
 * no token could be acquired — callers must not treat that as "send
 * unauthenticated".
 */
export async function getAccessToken(
  msalInstance: IPublicClientApplication,
  account: AccountInfo | null
): Promise<string | null> {
  // If authentication is disabled, no token needed
  if (!appConfig.enableAuthentication) {
    return null;
  }

  // If no account, can't get token
  if (!account) {
    return null;
  }

  try {
    // Try to get token silently first
    const response = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    console.error('Silent token acquisition failed:', error);

    try {
      // Fall back to interactive token acquisition
      return await acquireTokenInteractive(msalInstance);
    } catch (interactiveError) {
      console.error('Interactive token acquisition failed:', interactiveError);
      throw new AuthSessionExpiredError();
    }
  }
}

/**
 * Create authenticated headers
 * Adds Authorization header if token is available
 */
export function createAuthHeaders(token: string | null): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Make an authenticated API request
 * Automatically adds authentication token if available
 */
export async function authenticatedFetch<T>(
  msalInstance: IPublicClientApplication,
  account: AccountInfo | null,
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAccessToken(msalInstance, account);
  const url = endpoint.startsWith('http') 
    ? endpoint 
    : `${appConfig.apiUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      ...createAuthHeaders(token),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error(`API Error ${response.status}:`, errorText);
    
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorJson = JSON.parse(errorText);
      if (errorJson.errors) {
        const validationErrors = Object.entries(errorJson.errors)
          .map(([field, messages]) => `${field}: ${(messages as string[]).join(', ')}`)
          .join('; ');
        errorMessage = `Validation Error: ${validationErrors}`;
      } else if (errorJson.title) {
        errorMessage = errorJson.title;
      } else if (errorJson.error) {
        errorMessage = errorJson.error;
      }
    } catch {
      if (errorText) {
        errorMessage = errorText;
      }
    }
    
    throw new Error(errorMessage);
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return {} as T;
  }

  return response.json();
}
